import { Component, inject, OnInit, signal } from '@angular/core';
import { HomeService, VISIBILITY } from './home.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { concatMap, tap, timer } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import {
  LibGalleryCardComponent,
  LibKnowledgeCardComponent,
  LibNewsCardComponent,
  LibPromotionCardComponent,
  LibVideoPopupComponent,
} from '@web-nx-ng-tsc/web-promo-shared-ui';
import {
  LibAdsCarouselPopupComponent,
  LibBannerCarouselComponent,
  LibSearchBlockComponent,
} from '@web-nx-ng-tsc/web-promo-shared-pattern';
import { MatButton } from '@angular/material/button';
import {
  EProductCategory,
  IAllData,
  IImageGallery,
  IImageList,
  IKnowledge,
  IProductSearchOptions,
  IPromotionsRow,
} from '@web-nx-ng-tsc/web-shared-data-access';
import { ActivatedRoute, Router } from '@angular/router';
import { ROUTER_TOKENS } from '../../app.routes';
import { LS } from '@web-nx-ng-tsc/data-access';
import { LoggingService } from '@web-nx-ng-tsc/util-logging';
import { IMeta, MetaService } from '@web-nx-ng-tsc/util-meta';

@Component({
  selector: 'app-home',
  imports: [
    LibBannerCarouselComponent,
    LibSearchBlockComponent,
    LibPromotionCardComponent,
    LibKnowledgeCardComponent,
    LibNewsCardComponent,
    LibGalleryCardComponent,
    MatButton,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly metaService = inject(MetaService);
  private readonly homePageService = inject(HomeService);
  private readonly loggingService = inject(LoggingService);
  private readonly dialog = inject(MatDialog);
  private readonly observer = inject(BreakpointObserver);
  protected readonly ROUTER_TOKENS = ROUTER_TOKENS;

  isMobileViewSignal = signal<boolean>(false);

  bannerListSignal = this.homePageService.getBannerSignal();
  popupSignal = this.homePageService.getPopupSignal();
  promotionListSignal = this.homePageService.getPromotionListSignal();
  newsListSignal = this.homePageService.getNewsListSignal();
  videoListSignal = this.homePageService.getVideoListSignal();
  articleListSignal = this.homePageService.getArticleSignal();
  galleryListSignal = this.homePageService.getGalleryListSignal();

  visibilitySignal = signal<string>(VISIBILITY).asReadonly();
  categorySignal = signal<EProductCategory>(EProductCategory.TIRE).asReadonly();
  categoryIdSignal = signal<string>(this.homePageService.getCategoryId());

  carouselOptions = {
    loop: true,
    margin: 10,
    nav: false, // Enable navigation
    dots: false,
    autoplay: true,
    lazyLoad: false,
    autoplayTimeout: 3000,
    navText: ['<i class="fa fa-angle-left" style="align-content:center;"></i>', '<i class="fa fa-angle-right"></i>'],
    responsive: {
      0: {
        items: 1, // Show 1.5 items on smaller screens
      },
    },
  };

  bannerObject = signal<any>({
    carouselOptions: this.carouselOptions,
    carouselSlide: [],
  });

  get promotionRows() {
    const data = this.promotionListSignal();
    return (data?.rows as IPromotionsRow[]) || [];
  }

  constructor() {
    this.getAllData();

    this.metaService.updateMeta(this.route.snapshot.data as IMeta);
  }

  ngOnInit() {
    this.observer.observe('(max-width: 500px)').subscribe((res) => {
      this.isMobileViewSignal.set(res.matches);
      this.loggingService.log('Mobile?', this.isMobileViewSignal());
    });
  }

  getAllData() {
    this.homePageService.getBanners().subscribe(() => this.setBannerObject());
    this.homePageService.getPopups().subscribe((res: IAllData) => {
      if (res.rows.length) this.openDialog();
    });
    this.homePageService.getPromotionList().subscribe();
    this.homePageService.getNewsList().subscribe();
    this.homePageService.getVideoList().subscribe();
    this.homePageService.getAllArticle().subscribe();
    this.homePageService.getAllGallery().subscribe();

    if (!this.categoryIdSignal()) {
      this.getCategory();
    }
  }

  setBannerObject() {
    const { rows } = this.bannerListSignal()!;
    const currentBannerObj = this.bannerObject();
    currentBannerObj.carouselSlide = rows;
    this.bannerObject.set(currentBannerObj);
  }

  openDialog() {
    if (typeof window !== 'undefined') {
      timer(100).subscribe(() => {
        this.loggingService.error('###openDialog');
        const isSmallScreen = window.matchMedia('(max-width: 639px)').matches;

        const dialogRef = this.dialog.open(LibAdsCarouselPopupComponent, {
          height: isSmallScreen ? '440px' : ' auto',
          minHeight: isSmallScreen ? '440px' : '700px',
          disableClose: false,
          data: this.popupSignal,
        });
      });
    }
  }

  convertVideoData(videoAtt: any) {
    const { title, cover_image_url, excerpt, youtube_share_link } = videoAtt;
    const dataReturn: IKnowledge = {
      img: cover_image_url,
      title,
      excerpt,
      link_url: youtube_share_link,
      isVideo: true,
    };

    return dataReturn;
  }

  openPopupYoutube(url: string) {
    this.dialog.open(LibVideoPopupComponent, {
      // minWidth: '67%',
      // maxWidth: '100%',
      width: '90%',
      maxWidth: '1280px',
      // height: 'auto',
      data: { url: url },
    });
  }

  convertNewsData(newAtt: any) {
    const { id, cover_image_url, excerpt, title } = newAtt;
    const dataReturn: IImageList = {
      img: cover_image_url,
      title,
      excerpt,
      id,
    };

    return dataReturn;
  }

  convertGalleryData(newAtt: any) {
    const { id, images_url, description, name, hot_badge_enable } = newAtt;

    const dataReturn: IImageGallery = {
      img: images_url,
      name,
      description,
      hot_badge_enable,
      id,
    };

    return dataReturn;
  }

  routeToDetail(id: string, feature: string) {
    let path = '';
    switch (feature) {
      case 'promotions':
        path = `${ROUTER_TOKENS.PROMOTIONS}/detail/${id}`;
        break;
      case 'news':
        path = `${ROUTER_TOKENS.NEWS}/detail/${id}`;
        break;
      case 'article':
        path = `${ROUTER_TOKENS.ARTICLE}/detail/${id}`;
        break;
      default:
        break;
    }
    this.router.navigateByUrl(path);
  }

  routeToList(feature: string) {
    let path = '';
    switch (feature) {
      case 'promotions':
        path = `${ROUTER_TOKENS.PROMOTIONS}/list`;
        break;
      case 'video':
        path = `${ROUTER_TOKENS.VIDEOS}/list`;
        break;
      case 'news':
        path = `${ROUTER_TOKENS.NEWS}/list`;
        break;
      case 'article':
        path = `${ROUTER_TOKENS.ARTICLE}/list`;
        break;
      case 'gallery':
        path = `${ROUTER_TOKENS.GALLERY}/list`;
        break;
      default:
        break;
    }

    this.router.navigateByUrl(path);
  }

  handleSearchSize(e: IProductSearchOptions) {
    this.loggingService.log('FUNC | handleSearchSize event :', e);
    if (this.categoryIdSignal()) {
      this.homePageService
        .getCategory()
        .pipe(
          concatMap((res: any) => {
            const categoryState = res.rows.find((item: any) => item.key_name === 'tire');
            this.loggingService.log('FUNC | handleSearchSize event :', categoryState.id.toString());
            this.loggingService.log('FUNC | handleSearchSize event :', res.rows);

            this.categoryIdSignal.set(categoryState.id.toString());
            return this.getAllProductByCat(e);
          })
        )
        .subscribe();
    } else {
      this.getAllProductByCat(e).subscribe();
    }
  }

  getAllProductByCat(e: IProductSearchOptions) {
    this.loggingService.log('', e);
    return this.homePageService.getAllProductByCategory(this.categoryIdSignal(), e).pipe(
      tap(() => {
        const category = this.homePageService.getCategoryTire();
        this.homePageService.setStorage(LS.categoryState, category);
        this.router.navigate(['../', 'products', ROUTER_TOKENS.TIRE, 'list'], {
          state: { onClearData: true, dataSearch: e },
        });
      })
    );
  }

  getCategory() {
    this.homePageService.getCategory().subscribe((res: any) => {
      const categoryState = res.rows.find((item: any) => item.key_name === 'tire');
      this.categoryIdSignal.set(categoryState.id.toString());
    });
  }
}
